import { lazy, Suspense, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ChevronDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { useBabyData } from "@/hooks/useBabyData";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import LanguageSelector from "@/components/LanguageSelector";
const heroImage = "/hero-baby.jpg";
import { useTranslatedTexts } from "@/hooks/useTranslatedTexts";

const RemindersPanel = lazy(() => import("@/components/RemindersPanel"));
const AdBanner = lazy(() => import("@/components/AdBanner"));

const quickLinks = [
  { path: "/baby-games", labelKey: "games", emoji: "🕹️" },
  { path: "/baby-stories", labelKey: "bedtimeStories", emoji: "📖" },
  { path: "/baby-lullabies", labelKey: "lullabies", emoji: "🎵" },
  { path: "/baby-recipes", labelKey: "recipes", emoji: "🥣" },
  { path: "/baby-tips", labelKey: "tips", emoji: "💡" },
  { path: "/baby-sleep", labelKey: "sleep", emoji: "🌙" },
  { path: "/baby-feeding", labelKey: "feeding", emoji: "🍼" },
  { path: "/baby-medicine", labelKey: "medicine", emoji: "💊" },
];

const colorCycle = ["bg-lavender-light", "bg-peach-light", "bg-sage-light", "bg-cream", "bg-lavender-light", "bg-sage-light", "bg-peach-light", "bg-lavender-light"];

// Stable references to avoid re-triggering translation hooks
const NAV_TEXTS = ["Sign Out", "Sign In"];

const ABOUT_TEXTS = [
  "About Sweet Baby Bloom",
  "Sweet Baby Bloom",
  "is your all-in-one parenting companion — track sleep patterns, log feedings, set medicine reminders, and discover age-appropriate recipes, all in one place. Unlike other baby apps, we combine practical daily tracking with enriching content like lullabies, bedtime stories, and educational games to support both you and your little one. Available in",
  "50 languages",
  "and designed to be simple enough to use at 3 a.m., it's the only baby app that grows with your family — no clutter, no overwhelm, just everything you need.",
  "All-in-One Baby Tracker & Parenting Companion",
  "Track Sleep, Feedings & Milestones",
  "Smart Parenting Tools for Everyday Life",
  "Why Parents Choose Sweet Baby Bloom",
  // [9] already used above as tAbout[9]
];

const GUIDE_TEXTS = [
  "Free Parenting Guides",
  "The Complete Baby Sleep Guide",
  "Schedules, tips & solutions by age",
  "The Complete Baby Feeding Guide",
  "First foods, schedules & nutrition",
  "Baby Milestones Guide",
  "Month-by-month development tracker",
  "Baby Health & Safety Guide",
  "Fevers, first aid & childproofing",
  "Sleep Training Methods Compared",
  "CIO, Ferber, Fading & more",
  "Newborn Essentials Checklist",
  "Complete registry & buying guide",
  "Baby Care FAQ",
  "25+ common questions answered",
  "Browse All Guides →",
  "The Complete Breastfeeding Guide",
  "Latch, positions, supply & pumping",
];

const Index = () => {
  const { sleepEntries, feedingEntries } = useBabyData();
  const { t } = useLanguage();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { translated: tNav } = useTranslatedTexts(NAV_TEXTS, "nav-auth");
  const { translated: tAbout } = useTranslatedTexts(ABOUT_TEXTS, "about-section");
  const { translated: tGuide } = useTranslatedTexts(GUIDE_TEXTS, "guide-section");

  const today = useMemo(() => new Date().toISOString().split("T")[0], []);
  const todaySleep = useMemo(() => sleepEntries.filter((e) => e.date === today).reduce((s, e) => s + e.duration, 0), [sleepEntries, today]);
  const todayFeedings = useMemo(() => feedingEntries.filter((e) => e.date === today).length, [feedingEntries, today]);

  const formatDuration = (min: number) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  return (
    <>
      {/* Hero — fetchpriority high for LCP, explicit dimensions to prevent CLS */}
      <header className="relative overflow-hidden">
        <img
          src={heroImage}
          alt="Smiling baby sleeping peacefully in a soft blanket, representing Sweet Baby Bloom's sleep and feeding tracking features"
          className="h-48 md:h-72 lg:h-80 w-full object-cover"
          width={800}
          height={372}
          sizes="100vw"
          fetchPriority="high"
          decoding="sync"
        />
        <div className="absolute right-3 top-3 z-10 flex items-center gap-2 md:right-6 md:top-4">
          <LanguageSelector />
          {user ? (
            <Button variant="ghost" size="sm" onClick={signOut} className="rounded-full bg-card/80 backdrop-blur-sm text-xs font-semibold gap-1 px-3 min-h-11">
              <LogOut className="h-4 w-4" />
              {tNav[0]}
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => navigate("/auth")} className="rounded-full bg-card/80 backdrop-blur-sm text-xs font-semibold px-4 min-h-11 min-w-11">
              {tNav[1]}
            </Button>
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background" />
        <div className="absolute bottom-4 left-4 right-4 md:bottom-8 md:left-8 md:right-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground md:text-center">Sweet Baby Bloom 🦋</h1>
          <p className="text-sm md:text-base font-medium text-foreground/80 md:text-center">{tAbout[6]}</p>
        </div>
      </header>

    <main id="main-content" tabIndex={-1} className="min-h-screen bg-background pb-24 focus:outline-none">


      <div className="mx-auto max-w-lg md:max-w-3xl lg:max-w-5xl px-4 md:px-8 pt-4 md:pt-8">

        {/* About Section - full width on desktop, above the two-column grid */}
        <div className="md:mb-6 md:block hidden">
          <Collapsible className="mb-6 rounded-xl bg-card shadow-sm">
            <CollapsibleTrigger className="flex w-full items-center justify-center p-4">
              <span className="text-sm font-bold text-foreground">🦋 {tAbout[0]}</span>
              <ChevronDown className="ml-2 h-4 w-4 text-muted-foreground transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent className="px-4 pb-4">
              <p className="text-base leading-relaxed text-foreground/80 text-center">
                <span className="font-bold text-foreground">{tAbout[1]}</span> {tAbout[2]} <span className="font-semibold text-foreground">{tAbout[3]}</span> {tAbout[4]} 🦋
              </p>
            </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Desktop two-column layout for summary */}
        <div className="md:grid md:grid-cols-2 md:gap-8">
          {/* About Section - mobile only */}
          <div className="md:hidden">
            <Collapsible className="mb-6 rounded-xl bg-card shadow-sm">
              <CollapsibleTrigger className="flex w-full items-center justify-between p-4 min-h-11">
                <span className="text-sm font-bold text-foreground">🦋 {tAbout[0]}</span>
                <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent className="px-4 pb-4">
                <p className="text-base leading-relaxed text-foreground/80">
                  <span className="font-bold text-foreground">{tAbout[1]}</span> {tAbout[2]} <span className="font-semibold text-foreground">{tAbout[3]}</span> {tAbout[4]} 🦋
                </p>
              </CollapsibleContent>
            </Collapsible>
          </div>


          <div>
            {/* Today's Summary */}
            <h2 className="mb-3 text-lg font-bold text-foreground md:text-left">{tAbout[7]}</h2>
            <section aria-label="Today's summary" className="mb-6 grid grid-cols-2 gap-3">
              <Card className="border-none bg-lavender-light shadow-sm">
                <CardContent className="py-4 md:py-6 text-center">
                  <p className="text-2xl md:text-3xl font-bold text-foreground">{formatDuration(todaySleep)}</p>
                  <p className="text-xs md:text-sm font-medium text-muted-foreground">{t("sleepToday")}</p>
                </CardContent>
              </Card>
              <Card className="border-none bg-peach-light shadow-sm">
                <CardContent className="py-4 md:py-6 text-center">
                  <p className="text-2xl md:text-3xl font-bold text-foreground">{todayFeedings}</p>
                  <p className="text-xs md:text-sm font-medium text-muted-foreground">{t("feedingsToday")}</p>
                </CardContent>
              </Card>
            </section>
          </div>
        </div>



        {/* Quick Links */}
        <section aria-label="Quick access">
          <h2 className="mb-3 text-lg font-bold text-foreground">{tAbout[8]}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {quickLinks.map(({ path, labelKey, emoji }, i) => (
              <Link key={path} to={path} className="block min-h-11">
                <Card className={`border-none ${colorCycle[i]} shadow-sm transition-transform hover:scale-[1.02] active:scale-[0.98]`}>
                  <CardContent className="flex items-center gap-4 py-5">
                    <span className="text-4xl" role="img" aria-label={`${labelKey} icon`}>{emoji}</span>
                    <span className="text-sm font-bold text-foreground">{t(labelKey)}</span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>


        {/* Guides for SEO */}
        <section className="mt-6" aria-label="Parenting guides">
          <h2 className="text-lg font-bold text-foreground mb-3">📚 {tGuide[0]}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Link to="/baby-sleep-guide">
              <Card className="border-none bg-lavender-light shadow-sm transition-transform hover:scale-[1.01] active:scale-[0.99]">
                <CardContent className="py-4 md:py-6">
                  <p className="text-sm md:text-base font-bold text-foreground">🌙 {tGuide[1]}</p>
                  <p className="text-xs md:text-sm text-muted-foreground">{tGuide[2]}</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/baby-feeding-guide">
              <Card className="border-none bg-peach-light shadow-sm transition-transform hover:scale-[1.01] active:scale-[0.99]">
                <CardContent className="py-4 md:py-6">
                  <p className="text-sm md:text-base font-bold text-foreground">🍼 {tGuide[3]}</p>
                  <p className="text-xs md:text-sm text-muted-foreground">{tGuide[4]}</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/baby-milestones-guide">
              <Card className="border-none bg-sage-light shadow-sm transition-transform hover:scale-[1.01] active:scale-[0.99]">
                <CardContent className="py-4 md:py-6">
                  <p className="text-sm md:text-base font-bold text-foreground">👶 {tGuide[5]}</p>
                  <p className="text-xs md:text-sm text-muted-foreground">{tGuide[6]}</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/baby-health-guide">
              <Card className="border-none bg-lavender-light shadow-sm transition-transform hover:scale-[1.01] active:scale-[0.99]">
                <CardContent className="py-4 md:py-6">
                  <p className="text-sm md:text-base font-bold text-foreground">🏥 {tGuide[7]}</p>
                  <p className="text-xs md:text-sm text-muted-foreground">{tGuide[8]}</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/sleep-training-guide">
              <Card className="border-none bg-peach-light shadow-sm transition-transform hover:scale-[1.01] active:scale-[0.99]">
                <CardContent className="py-4 md:py-6">
                  <p className="text-sm md:text-base font-bold text-foreground">😴 {tGuide[9]}</p>
                  <p className="text-xs md:text-sm text-muted-foreground">{tGuide[10]}</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/newborn-checklist">
              <Card className="border-none bg-sage-light shadow-sm transition-transform hover:scale-[1.01] active:scale-[0.99]">
                <CardContent className="py-4 md:py-6">
                  <p className="text-sm md:text-base font-bold text-foreground">✅ {tGuide[11]}</p>
                  <p className="text-xs md:text-sm text-muted-foreground">{tGuide[12]}</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/baby-faq">
              <Card className="border-none bg-lavender-light shadow-sm transition-transform hover:scale-[1.01] active:scale-[0.99]">
                <CardContent className="py-4 md:py-6">
                  <p className="text-sm md:text-base font-bold text-foreground">❓ {tGuide[13]}</p>
                  <p className="text-xs md:text-sm text-muted-foreground">{tGuide[14]}</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/breastfeeding-guide">
              <Card className="border-none bg-peach-light shadow-sm transition-transform hover:scale-[1.01] active:scale-[0.99]">
                <CardContent className="py-4 md:py-6">
                  <p className="text-sm md:text-base font-bold text-foreground">🤱 {tGuide[16]}</p>
                  <p className="text-xs md:text-sm text-muted-foreground">{tGuide[17]}</p>
                </CardContent>
              </Card>
            </Link>
          </div>
          <Link to="/articles" className="mt-3 inline-flex w-full items-center justify-center min-h-11 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors">
            {tGuide[15]}
          </Link>

        </section>

        {/* Ad */}
        <Suspense fallback={null}>
          <div className="mt-6">
            <AdBanner size="inline" />
          </div>
        </Suspense>

        {/* Reminders */}
        <Suspense fallback={null}>
          <section aria-label="Reminders" className="mt-6">
            <RemindersPanel />
          </section>
        </Suspense>

        {/* Recent Activity */}
        {(sleepEntries.length > 0 || feedingEntries.length > 0) && (
          <section aria-label="Recent activity" className="mt-6">
            <h2 className="mb-3 text-lg font-bold text-foreground">{t("recentActivity")}</h2>
            <div className="space-y-2">
              {sleepEntries.slice(0, 3).map((e) => (
                <Card key={e.id} className="border-none shadow-sm">
                  <CardContent className="flex items-center gap-3 py-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-lavender-light text-sm" role="img" aria-label={e.type === "night" ? "Night sleep" : "Daytime nap"}>
                      {e.type === "night" ? "🌙" : "☀️"}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {t("slept")} {formatDuration(e.duration)}
                      </p>
                      <p className="text-xs text-muted-foreground">{e.date} · {e.startTime}–{e.endTime}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {feedingEntries.slice(0, 2).map((e) => (
                <Card key={e.id} className="border-none shadow-sm">
                  <CardContent className="flex items-center gap-3 py-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-peach-light text-sm" role="img" aria-label="Feeding entry">🍼</div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {e.type.charAt(0).toUpperCase() + e.type.slice(1)} {e.amount && `· ${e.amount}`}
                      </p>
                      <p className="text-xs text-muted-foreground">{e.date} at {e.time}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}
        {/* Bottom Ad */}
        <Suspense fallback={null}>
          <div className="mt-6">
            <AdBanner size="rectangle" />
          </div>
        </Suspense>

      </div>
    </main>
    </>
  );
};

export default Index;
